import React, { useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { SchemaTable } from '../types';

interface Props {
  schema: SchemaTable[];
  loading?: boolean;
  onSelectTable?: (tableName: string) => void;
}

const isTrx = (name: string) => name.startsWith('t_');

const SaktiRemoteSchemaTree: React.FC<Props> = ({ schema, loading, onSelectTable }) => {
  const [activeTable, setActiveTable] = useState<string | null>(null);
  const [trxOnly, setTrxOnly] = useState(false);

  if (loading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="sm" color="customGreen.500" />
        <Text fontSize="sm" color="gray.500" mt={2}>Memuat schema…</Text>
      </Box>
    );
  }

  if (schema.length === 0) {
    return (
      <Box p={4}>
        <Text fontSize="sm" color="gray.400" textAlign="center">
          Belum ada schema. Klik "Muat Schema" untuk memulai.
        </Text>
      </Box>
    );
  }

  // t_* tables (writable transaction tables) sorted first, then the rest alphabetically
  const sorted = [...schema].sort((a, b) => {
    const rank = (n: string) => (isTrx(n) ? 0 : 1);
    return rank(a.name) - rank(b.name) || a.name.localeCompare(b.name);
  });
  const visible = trxOnly ? sorted.filter((t) => isTrx(t.name)) : sorted;

  return (
    <Box>
      <HStack mb={2} spacing={2}>
        <Button
          size="xs"
          variant={trxOnly ? 'solid' : 'outline'}
          colorScheme="green"
          onClick={() => setTrxOnly((v) => !v)}
        >
          {trxOnly ? 'Semua tabel' : 'Hanya t_*'}
        </Button>
        <Text fontSize="xs" color="gray.400">{visible.length} tabel</Text>
      </HStack>

      <Accordion allowMultiple reduceMotion>
        {visible.map((table) => {
          const trx = isTrx(table.name);
          return (
            <AccordionItem key={table.name} border="none" mb={1}>
              <AccordionButton
                px={3}
                py={2}
                borderRadius="md"
                bg={activeTable === table.name ? 'green.50' : 'transparent'}
                _hover={{ bg: 'gray.50' }}
                onClick={() => {
                  setActiveTable(table.name);
                  onSelectTable?.(table.name);
                }}
              >
                <HStack flex={1} spacing={2} textAlign="left">
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={trx ? 'green.700' : 'gray.500'}
                    fontFamily="mono"
                  >
                    {table.name}
                  </Text>
                  {trx && (
                    <Badge colorScheme="green" fontSize="0.55rem" variant="subtle">TRX</Badge>
                  )}
                  <Badge colorScheme="gray" fontSize="0.6rem">{table.columns.length} col</Badge>
                </HStack>
                <AccordionIcon color="gray.400" />
              </AccordionButton>
              <AccordionPanel pb={2} px={3}>
                <VStack align="stretch" spacing={0}>
                  {table.columns.map((col) => (
                    <HStack key={col.name} py={1} spacing={2} borderBottomWidth="1px" borderBottomColor="gray.100">
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        color={col.primary_key ? 'green.600' : 'gray.700'}
                        fontWeight={col.primary_key ? 'bold' : 'normal'}
                        flex={1}
                      >
                        {col.name}
                      </Text>
                      <Text fontSize="xs" color="gray.400" fontFamily="mono">{col.type}</Text>
                      {col.nullable && <Badge colorScheme="orange" fontSize="0.55rem">null</Badge>}
                      {col.primary_key && <Badge colorScheme="green" fontSize="0.55rem">PK</Badge>}
                    </HStack>
                  ))}
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Box>
  );
};

export default SaktiRemoteSchemaTree;
